import React, { useMemo, useState } from 'react';

import { Refresh, Search } from '@mui/icons-material';
import { IconButton, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material';

import useCurrentUser from '~/hooks/useCurrentUser';

import ChatterComposer from './ChatterComposer';
import ChatterPostCard from './ChatterPostCard';
import { CHATTER_SORT_OPTIONS } from './constants';
import { ChatterDraft, ChatterPost, ChatterSortOrder } from './types';
import { getCreatedTime, getLastActivityTime } from './utils';

const FALLBACK_AUTHOR_NAME = 'Admin user';

interface Props {
  initialPosts: Array<ChatterPost>;
  scopeLabel: string;
}

export default function ChatterFeed({ initialPosts, scopeLabel }: Props) {
  const { user } = useCurrentUser();
  const currentUserName = user?.fullName || FALLBACK_AUTHOR_NAME;

  const [posts, setPosts] = useState<Array<ChatterPost>>(initialPosts);
  const [sortOrder, setSortOrder] = useState<ChatterSortOrder>('activity');
  const [searchQuery, setSearchQuery] = useState('');

  const visiblePosts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matching = posts.filter((post) => !query || `${post.authorName} ${post.text}`.toLowerCase().includes(query));
    const getSortTime = sortOrder === 'latest' ? getCreatedTime : getLastActivityTime;
    return [...matching].sort((a, b) => getSortTime(b) - getSortTime(a));
  }, [posts, searchQuery, sortOrder]);

  const handleShare = ({ text, audience, attachments }: ChatterDraft) => {
    const newPost: ChatterPost = {
      id: `post-${Date.now()}`,
      authorName: currentUserName,
      audience,
      createdAt: new Date().toISOString(),
      text,
      attachments,
      likeCount: 0,
      isLikedByMe: false,
      comments: [],
    };
    setPosts((prev) => [newPost, ...prev]);
  };

  const handleToggleLike = (postId: string) => {
    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId
          ? {
              ...post,
              isLikedByMe: !post.isLikedByMe,
              likeCount: Math.max(0, post.likeCount + (post.isLikedByMe ? -1 : 1)),
            }
          : post,
      ),
    );
  };

  const handleAddComment = (postId: string, text: string) => {
    const comment = {
      id: `comment-${Date.now()}`,
      authorName: currentUserName,
      createdAt: new Date().toISOString(),
      text,
    };
    setPosts((prev) =>
      prev.map((post) => (post.id === postId ? { ...post, comments: [...post.comments, comment] } : post)),
    );
  };

  return (
    <Stack spacing={2} maxWidth={980}>
      <ChatterComposer scopeLabel={scopeLabel} onShare={handleShare} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">Sort by:</Typography>
          <TextField
            select
            size="small"
            value={sortOrder}
            onChange={(event) => setSortOrder(event.target.value as ChatterSortOrder)}
            slotProps={{ htmlInput: { 'aria-label': 'Sort feed' } }}
          >
            {CHATTER_SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <TextField
            type="search"
            size="small"
            placeholder="Search this feed..."
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <IconButton aria-label="Refresh" onClick={() => setSearchQuery('')}>
            <Refresh />
          </IconButton>
        </Stack>
      </Stack>
      {visiblePosts.length === 0 ? (
        <Typography color="text.secondary" align="center" py={4}>
          {searchQuery.trim()
            ? 'No posts match that search.'
            : 'No posts yet. Share an update to start the conversation.'}
        </Typography>
      ) : (
        visiblePosts.map((post) => (
          <ChatterPostCard
            key={post.id}
            post={post}
            currentUserName={currentUserName}
            onToggleLike={handleToggleLike}
            onAddComment={handleAddComment}
          />
        ))
      )}
    </Stack>
  );
}
